import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Music, ArrowLeft, Eye, EyeOff, CheckCircle, AlertTriangle, Lock } from "lucide-react";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  // iter-93: same page handles both flows. `?setup=1` flips copy from
  // "Reset Password" to "Set up your account" so brand-new deps don't get
  // confused by reset-flow phrasing.
  const isSetup = searchParams.get("setup") === "1";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError(isSetup
        ? "Invalid or missing setup link. Please ask your band admin to re-send your invitation."
        : "Invalid or missing reset token. Please request a new password reset.");
    }
  }, [token, isSetup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await apiClient.post("/auth/reset-password", { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password. The link may have expired.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="spotlight absolute inset-0 pointer-events-none" />
      
      <div className="w-full max-w-md space-y-8 relative z-10 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/30 mb-6">
            <Music className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">
            {success
              ? (isSetup ? "Welcome aboard!" : "Password Reset!")
              : error
                ? (isSetup ? "Setup Failed" : "Reset Failed")
                : (isSetup ? "Set up your account" : "Reset Password")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {success
              ? (isSetup
                  ? "Your account is ready — sign in to see your invitation."
                  : "You can now sign in with your new password.")
              : error
                ? ""
                : (isSetup
                    ? "Choose a password to finish setting up your dep account."
                    : "Enter your new password below.")}
          </p>
        </div>

        {success ? (
          <Card className="glass-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-heading font-semibold mb-2">All Set!</h2>
              <p className="text-muted-foreground mb-6">
                Your password has been successfully reset.
              </p>
              <Link to="/login">
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                  Sign In Now
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="glass-card border-red-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-heading font-semibold mb-2 text-red-500">Reset Failed</h2>
              <p className="text-muted-foreground mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <Link to="/forgot-password">
                  <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                    Request New Reset Link
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card className="glass-card">
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    New Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-secondary/50 border-transparent focus:border-primary pl-10 pr-10"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-secondary/50 border-transparent focus:border-primary pl-10"
                      required
                      minLength={6}
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Password must be at least 6 characters long.
                </p>
              </CardContent>
            </Card>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium btn-glow"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading
                ? (isSetup ? "Setting up..." : "Resetting...")
                : (isSetup ? "Set Password & Continue" : "Reset Password")}
            </Button>
            
            <Link to="/login">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
